import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ViewerSettings } from "../../types/settings";

type LanguageCode = ViewerSettings["language"];

const SUPPORT_MODAL_COPY: Record<
  LanguageCode,
  {
    title: string;
    subtitle: string;
    closeButtonText: string;
    primaryButtonText: string;
    closeModal: string;
    documentationDetail: string;
    options: Array<{
      title: string;
      description: string;
      detail?: string;
    }>;
  }
> = {
  en: {
    title: "Customer Support",
    subtitle: "Choose the fastest way to get help",
    closeButtonText: "Close",
    primaryButtonText: "View Documentation",
    closeModal: "Close customer support modal",
    documentationDetail: "Open support documentation",
    options: [
      {
        title: "Call Customer Support",
        description:
          "Speak directly with a support specialist for urgent product assistance.",
      },
      {
        title: "Send an Email",
        description:
          "Email the support team and receive follow-up guidance for your issue.",
      },
      {
        title: "Ask the Chatbot",
        description:
          "Get quick answers, troubleshooting help, and product guidance instantly.",
        detail: "Open automated support assistant",
      },
      {
        title: "View Product Documentation",
        description:
          "Browse setup guides, troubleshooting steps, and technical reference material.",
      },
    ],
  },
  es: {
    title: "Soporte al cliente",
    subtitle: "Elige la forma m\u00e1s r\u00e1pida de obtener ayuda",
    closeButtonText: "Cerrar",
    primaryButtonText: "Ver documentaci\u00f3n",
    closeModal: "Cerrar modal de soporte al cliente",
    documentationDetail: "Abrir documentaci\u00f3n de soporte",
    options: [
      {
        title: "Llamar a soporte al cliente",
        description:
          "Habla directamente con un especialista de soporte para obtener ayuda urgente con el producto.",
      },
      {
        title: "Enviar un correo electr\u00f3nico",
        description:
          "Escribe al equipo de soporte y recibe orientaci\u00f3n de seguimiento para tu problema.",
      },
      {
        title: "Preguntar al chatbot",
        description:
          "Obt\u00e9n respuestas r\u00e1pidas, ayuda para solucionar problemas y orientaci\u00f3n del producto al instante.",
        detail: "Abrir asistente de soporte automatizado",
      },
      {
        title: "Ver documentaci\u00f3n del producto",
        description:
          "Consulta gu\u00edas de configuraci\u00f3n, pasos de soluci\u00f3n de problemas y material t\u00e9cnico de referencia.",
      },
    ],
  },
  fr: {
    title: "Support client",
    subtitle:
      "Choisissez le moyen le plus rapide d\u2019obtenir de l\u2019aide",
    closeButtonText: "Fermer",
    primaryButtonText: "Voir la documentation",
    closeModal: "Fermer la fen\u00eatre du support client",
    documentationDetail: "Ouvrir la documentation de support",
    options: [
      {
        title: "Appeler le support client",
        description:
          "Parlez directement \u00e0 un sp\u00e9cialiste du support pour une aide urgente sur le produit.",
      },
      {
        title: "Envoyer un e-mail",
        description:
          "Contactez l\u2019\u00e9quipe de support par e-mail et recevez des conseils de suivi pour votre probl\u00e8me.",
      },
      {
        title: "Demander au chatbot",
        description:
          "Obtenez rapidement des r\u00e9ponses, de l\u2019aide au d\u00e9pannage et des conseils produit.",
        detail: "Ouvrir l\u2019assistant de support automatis\u00e9",
      },
      {
        title: "Voir la documentation produit",
        description:
          "Parcourez les guides d\u2019installation, les \u00e9tapes de d\u00e9pannage et les r\u00e9f\u00e9rences techniques.",
      },
    ],
  },
  de: {
    title: "Kundensupport",
    subtitle: "W\u00e4hlen Sie den schnellsten Weg, um Hilfe zu erhalten",
    closeButtonText: "Schlie\u00dfen",
    primaryButtonText: "Dokumentation anzeigen",
    closeModal: "Kundensupport-Dialog schlie\u00dfen",
    documentationDetail: "Support-Dokumentation \u00f6ffnen",
    options: [
      {
        title: "Kundensupport anrufen",
        description:
          "Sprechen Sie direkt mit einem Support-Spezialisten f\u00fcr dringende Produkthilfe.",
      },
      {
        title: "E-Mail senden",
        description:
          "Schreiben Sie dem Support-Team und erhalten Sie im Anschluss Hilfestellung zu Ihrem Problem.",
      },
      {
        title: "Den Chatbot fragen",
        description:
          "Erhalten Sie sofort schnelle Antworten, Hilfe bei der Fehlerbehebung und Produkthinweise.",
        detail: "Automatischen Support-Assistenten \u00f6ffnen",
      },
      {
        title: "Produktdokumentation ansehen",
        description:
          "Durchsuchen Sie Einrichtungsanleitungen, Schritte zur Fehlerbehebung und technisches Referenzmaterial.",
      },
    ],
  },
  zh: {
    title: "\u5ba2\u6237\u652f\u6301",
    subtitle:
      "\u9009\u62e9\u83b7\u53d6\u5e2e\u52a9\u7684\u6700\u5feb\u65b9\u5f0f",
    closeButtonText: "\u5173\u95ed",
    primaryButtonText: "\u67e5\u770b\u6587\u6863",
    closeModal: "\u5173\u95ed\u5ba2\u6237\u652f\u6301\u7a97\u53e3",
    documentationDetail: "\u6253\u5f00\u652f\u6301\u6587\u6863",
    options: [
      {
        title: "\u81f4\u7535\u5ba2\u6237\u652f\u6301",
        description:
          "\u76f4\u63a5\u4e0e\u652f\u6301\u4e13\u5458\u901a\u8bdd\uff0c\u83b7\u53d6\u7d27\u6025\u4ea7\u54c1\u5e2e\u52a9\u3002",
      },
      {
        title: "\u53d1\u9001\u7535\u5b50\u90ae\u4ef6",
        description:
          "\u7ed9\u652f\u6301\u56e2\u961f\u53d1\u90ae\u4ef6\uff0c\u83b7\u53d6\u9488\u5bf9\u60a8\u95ee\u9898\u7684\u540e\u7eed\u6307\u5bfc\u3002",
      },
      {
        title: "\u8be2\u95ee\u804a\u5929\u673a\u5668\u4eba",
        description:
          "\u7acb\u5373\u83b7\u5f97\u5feb\u901f\u56de\u7b54\u3001\u6545\u969c\u6392\u9664\u5e2e\u52a9\u548c\u4ea7\u54c1\u6307\u5f15\u3002",
        detail: "\u6253\u5f00\u81ea\u52a8\u652f\u6301\u52a9\u624b",
      },
      {
        title: "\u67e5\u770b\u4ea7\u54c1\u6587\u6863",
        description:
          "\u6d4f\u89c8\u8bbe\u7f6e\u6307\u5357\u3001\u6545\u969c\u6392\u9664\u6b65\u9aa4\u548c\u6280\u672f\u53c2\u8003\u8d44\u6599\u3002",
      },
    ],
  },
};

interface SupportOption {
  id: string;
  icon: string;
  title: string;
  description: string;
  detail?: string;
  onClick?: () => void;
}

interface SupportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCallSupport?: () => void;
  onEmailSupport?: () => void;
  onOpenChatbot?: () => void;
  onViewDocumentation?: () => void;
  supportPhone?: string;
  supportEmail?: string;
  documentationLabel?: string;
  language?: LanguageCode;
  title?: string;
  subtitle?: string;
  titleIcon?: string;
  closeButtonText?: string;
  primaryButtonText?: string;
  backgroundColor?: string;
  backdropBlur?: string;
  borderColor?: string;
  boxShadow?: string;
  primaryTextColor?: string;
  secondaryTextColor?: string;
  iconColor?: string;
  accentColor?: string;
  fontFamily?: string;
}

export function SupportModal({
  isOpen,
  onClose,
  onCallSupport,
  onEmailSupport,
  onOpenChatbot,
  onViewDocumentation,
  supportPhone = "+1 (800) 555-0123",
  supportEmail = "support@unit04manual.com",
  documentationLabel,
  language = "en",
  title,
  subtitle,
  titleIcon = "support_agent",
  closeButtonText,
  primaryButtonText,
  backgroundColor = "rgba(255, 255, 255, 0.04)",
  backdropBlur = "40px",
  borderColor = "rgba(255, 255, 255, 0.1)",
  boxShadow = "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
  primaryTextColor = "rgba(255, 255, 255, 0.95)",
  secondaryTextColor = "rgba(255, 255, 255, 0.7)",
  iconColor = "rgba(255, 255, 255, 0.8)",
  accentColor = "rgb(56, 189, 248)",
  fontFamily = "Arial, sans-serif",
}: SupportModalProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const copy = SUPPORT_MODAL_COPY[language] ?? SUPPORT_MODAL_COPY.en;
  const resolvedDocumentationLabel =
    documentationLabel ?? copy.documentationDetail;
  const resolvedTitle = title ?? copy.title;
  const resolvedSubtitle = subtitle ?? copy.subtitle;
  const resolvedCloseButtonText = closeButtonText ?? copy.closeButtonText;
  const resolvedPrimaryButtonText = primaryButtonText ?? copy.primaryButtonText;

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      const openTimer = window.setTimeout(() => setIsVisible(true), 10);
      return () => window.clearTimeout(openTimer);
    }

    setIsVisible(false);
    const closeTimer = window.setTimeout(() => setShouldRender(false), 300);
    return () => window.clearTimeout(closeTimer);
  }, [isOpen]);

  const options: SupportOption[] = [
    {
      id: "call",
      icon: "call",
      title: copy.options[0].title,
      description: copy.options[0].description,
      detail: supportPhone,
      onClick: onCallSupport,
    },
    {
      id: "email",
      icon: "mail",
      title: copy.options[1].title,
      description: copy.options[1].description,
      detail: supportEmail,
      onClick: onEmailSupport,
    },
    {
      id: "chatbot",
      icon: "smart_toy",
      title: copy.options[2].title,
      description: copy.options[2].description,
      detail: copy.options[2].detail,
      onClick: onOpenChatbot,
    },
    {
      id: "documentation",
      icon: "menu_book",
      title: copy.options[3].title,
      description: copy.options[3].description,
      detail: resolvedDocumentationLabel,
      onClick: onViewDocumentation,
    },
  ];

  if (!shouldRender) return null;

  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 230,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(15, 17, 21, 0.6)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        opacity: isVisible ? 1 : 0,
        transition: "opacity 0.3s ease-in-out",
        fontFamily,
        padding: "1.5rem",
      }}
      onClick={onClose}
      role="presentation"
      data-oid="z8mrl5o"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="support-modal-title"
        aria-describedby="support-modal-description"
        style={{
          width: "540px",
          maxWidth: "90vw",
          borderRadius: "2rem",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          backgroundColor,
          backdropFilter: `blur(${backdropBlur})`,
          WebkitBackdropFilter: `blur(${backdropBlur})`,
          border: `1px solid ${borderColor}`,
          boxShadow,
          transform: isVisible ? "scale(1)" : "scale(0.95)",
          transition: "transform 0.3s ease-in-out",
        }}
        onClick={(e) => e.stopPropagation()}
        data-oid="vcdrw:-"
      >
        <button
          onClick={onClose}
          aria-label={copy.closeModal}
          style={{
            position: "absolute",
            top: "1.5rem",
            right: "1.5rem",
            width: "2.5rem",
            height: "2.5rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "9999px",
            background: "transparent",
            border: "none",
            color: secondaryTextColor,
            cursor: "pointer",
            transition: "all 0.3s",
            zIndex: 10,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
            e.currentTarget.style.color = primaryTextColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = secondaryTextColor;
          }}
          data-oid="oll47fa"
        >
          <span
            className="material-symbols-outlined"
            style={{ fontSize: "1.25rem", fontWeight: 300 }}
            data-oid="djg1wn:"
          >
            close
          </span>
        </button>

        <div style={{ padding: "2rem 2rem 1rem 2rem" }} data-oid=":ozj7su">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              marginBottom: "0.25rem",
            }}
            data-oid="lbh8ezo"
          >
            <span
              className="material-symbols-outlined"
              style={{
                color: accentColor,
                fontSize: "1.5rem",
                fontWeight: 300,
              }}
              data-oid="ag5eah8"
            >
              {titleIcon}
            </span>
            <h2
              id="support-modal-title"
              style={{
                margin: 0,
                fontSize: "1.25rem",
                fontWeight: 700,
                letterSpacing: "0.025em",
                color: primaryTextColor,
                fontFamily,
              }}
              data-oid="6176se2"
            >
              {resolvedTitle}
            </h2>
          </div>
          <p
            id="support-modal-description"
            style={{
              margin: 0,
              fontSize: "0.875rem",
              fontWeight: 400,
              color: secondaryTextColor,
              fontFamily,
            }}
            data-oid="e4c.zfr"
          >
            {resolvedSubtitle}
          </p>
        </div>

        <div
          style={{
            padding: "1rem 2rem",
            display: "flex",
            flexDirection: "column",
            gap: "0.75rem",
            overflowY: "auto",
            maxHeight: "65vh",
          }}
          data-oid="6ct6gd2"
        >
          {options.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={option.onClick}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "1rem",
                width: "100%",
                padding: "1rem",
                borderRadius: "1rem",
                background: "rgba(255, 255, 255, 0.03)",
                border: "1px solid rgba(255, 255, 255, 0.05)",
                transition: "all 0.3s",
                textAlign: "left",
                cursor: option.onClick ? "pointer" : "default",
              }}
              onMouseEnter={(e) => {
                if (!option.onClick) return;
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.06)";
                e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.1)";
              }}
              onMouseLeave={(e) => {
                if (!option.onClick) return;
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.03)";
                e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.05)";
              }}
              data-oid="arh.cw8"
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  minWidth: 0,
                }}
                data-oid="5tst408"
              >
                <div
                  style={{
                    width: "3rem",
                    height: "3rem",
                    borderRadius: "0.75rem",
                    background: `${accentColor}1A`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                  data-oid="zxm3ly7"
                >
                  <span
                    className="material-symbols-outlined"
                    style={{
                      color: iconColor,
                      fontSize: "1.5rem",
                      fontWeight: 300,
                    }}
                    data-oid="d0c8i6y"
                  >
                    {option.icon}
                  </span>
                </div>
                <div style={{ minWidth: 0 }} data-oid="8uf1au3">
                  <h4
                    style={{
                      margin: 0,
                      marginBottom: "0.125rem",
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      color: primaryTextColor,
                      fontFamily,
                    }}
                    data-oid=".nljue9"
                  >
                    {option.title}
                  </h4>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "0.75rem",
                      fontWeight: 400,
                      color: secondaryTextColor,
                      lineHeight: 1.6,
                      fontFamily,
                    }}
                    data-oid="v2yoyy:"
                  >
                    {option.description}
                  </p>
                  {option.detail && (
                    <div
                      style={{
                        marginTop: "0.4rem",
                        fontSize: "0.6875rem",
                        fontWeight: 600,
                        color: `${accentColor}`,
                        letterSpacing: "0.02em",
                      }}
                      data-oid="xpjgzyv"
                    >
                      {option.detail}
                    </div>
                  )}
                </div>
              </div>

              <span
                className="material-symbols-outlined"
                style={{
                  fontSize: "1.125rem",
                  fontWeight: 300,
                  color: "rgba(255, 255, 255, 0.45)",
                  flexShrink: 0,
                }}
                data-oid="7h4d9q9"
              >
                arrow_forward_ios
              </span>
            </button>
          ))}
        </div>

        <div
          style={{
            padding: "2rem",
            marginTop: "0.5rem",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "1.5rem",
            borderTop: "1px solid rgba(255, 255, 255, 0.05)",
          }}
          data-oid="huym698"
        >
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              fontSize: "0.75rem",
              fontWeight: 500,
              color: secondaryTextColor,
              cursor: "pointer",
              transition: "color 0.3s",
              fontFamily,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = primaryTextColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = secondaryTextColor;
            }}
            data-oid="k3p_hlx"
          >
            {resolvedCloseButtonText}
          </button>
          <button
            onClick={onViewDocumentation}
            style={{
              padding: "0.75rem 2.5rem",
              fontSize: "0.75rem",
              fontWeight: 700,
              borderRadius: "9999px",
              background: accentColor,
              color: "white",
              border: "none",
              cursor: "pointer",
              boxShadow: `0 0 20px ${accentColor}4D`,
              transition: "all 0.3s",
              fontFamily,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = accentColor.replace(
                "248",
                "228",
              );
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = accentColor;
              e.currentTarget.style.transform = "translateY(0)";
            }}
            data-oid="vmo9-_j"
          >
            {resolvedPrimaryButtonText}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
